import React from 'react'
import { Text, View } from '@react-pdf/renderer'
import moment from 'moment'
import {
  ChartImage,
  CountTable,
  InPairs,
  DataTable,
  INK,
  LINE,
  MUTED,
  NAVY,
  ReportPage,
  SectionHeading,
  SectionTitle,
  StatRow,
  TEAL,
  clean,
  fmtDateTime,
  fmtHour,
  fmtMinutes,
  topEntries,
} from '../../../../../../components/PDF/pdfKit'

export const INTERVALS = [
  ['callToDispatch', 'Call to dispatch', 'Call logged to the first resource assigned'],
  ['dispatchToMobile', 'Dispatch to mobile', 'First resource assigned to that resource mobile'],
  ['callToScene', 'Call to first on scene', 'Call logged to the first resource arriving (response time)'],
  ['onScene', 'Time on scene', 'First resource arriving to that resource leaving scene'],
  ['jobCycle', 'Job cycle', 'Call logged to the last resource clear'],
]

const PRIORITY_ORDER = ['Critical', 'High', 'Moderate', 'Low']

export const ControlRoomPerformance = ({ number, data, chrome, charts, include }) => {
  const room = data.controlRoom
  const s = room.summary
  const part = (key) => include.part('controlRoom', key)
  return (
    <ReportPage chrome={chrome}>
      <SectionHeading
        number={number}
        title="Control room performance"
        intro={`Incidents managed through the event control room${room.ref ? ` (${room.ref})` : ''}, from first call to the last resource clearing. Cancelled incidents are counted but excluded from the timings.`}
      />
      {part('headline') && (
        <StatRow
          items={[
            { label: 'Incidents', value: s.total, sub: s.cancelled ? `${s.cancelled} cancelled` : null },
            { label: 'Median response', value: fmtMinutes(s.intervals.callToScene.median), sub: 'call to first on scene', color: TEAL },
            ...(part('intervals') ? [{ label: '90% responded within', value: fmtMinutes(s.intervals.callToScene.p90) }] : []),
            { label: 'Busiest hour', value: fmtHour(s.peakHour?.hour), sub: s.peakHour ? `${s.peakHour.count} incidents` : null },
            { label: 'Control room open', value: `${room.open.hours} h` },
          ]}
        />
      )}

      {part('intervals') && (
        <>
          <SectionTitle>Response intervals</SectionTitle>
          <DataTable
            columns={[
              { label: 'Interval', flex: 1.6 },
              { label: 'Definition', flex: 3 },
              { label: 'Measured', flex: 0.8, align: 'right' },
              { label: 'Median', flex: 0.9, align: 'right' },
              { label: 'Mean', flex: 0.9, align: 'right' },
              { label: '90th centile', flex: 1, align: 'right' },
            ]}
            rows={INTERVALS.map(([key, label, definition]) => {
              const v = s.intervals[key]
              return [label, definition, v.count, fmtMinutes(v.median), fmtMinutes(v.mean), fmtMinutes(v.p90)]
            })}
          />
        </>
      )}

      {part('priority') && (
        <>
          <SectionTitle>Response by priority</SectionTitle>
          <DataTable
            columns={[
              { label: 'Priority', flex: 1 },
              { label: 'Incidents', flex: 0.8, align: 'right' },
              { label: 'Median response', flex: 1.2, align: 'right' },
              { label: '90th centile response', flex: 1.3, align: 'right' },
              { label: 'Median on scene', flex: 1.2, align: 'right' },
              { label: 'Median job cycle', flex: 1.2, align: 'right' },
            ]}
            rows={PRIORITY_ORDER.filter((p) => s.responseByPriority[p]).map((p) => {
              const r = s.responseByPriority[p]
              return [p, r.incidents, fmtMinutes(r.callToScene.median), fmtMinutes(r.callToScene.p90), fmtMinutes(r.onScene.median), fmtMinutes(r.jobCycle.median)]
            })}
          />
        </>
      )}

      {part('hourly') && (
        <>
          <SectionTitle>Incidents by hour of day</SectionTitle>
          <ChartImage image={charts.cadHourly} caption="All operational days combined. Hours run from 05:00." />
        </>
      )}

      <InPairs
        blocks={[
          part('types') && (
            <>
              <SectionTitle>Incident type</SectionTitle>
              <CountTable rows={topEntries(s.byType)} total={s.total} />
            </>
          ),
          part('triage') && (
            <>
              <SectionTitle>Call triage category</SectionTitle>
              <CountTable rows={topEntries(s.triageCategories, 8)} total={s.total} emptyText="Calls were not triaged" />
            </>
          ),
        ]}
      />

      {part('resources') && (
        <>
          <SectionTitle>Resources</SectionTitle>
          <Text style={{ fontSize: 6.5, color: MUTED, marginBottom: 5, lineHeight: 1.4 }}>
            {clean(
              `${s.multiResource} incident${s.multiResource === 1 ? '' : 's'} needed more than one resource. ${s.hospitalArrivals} incident${s.hospitalArrivals === 1 ? '' : 's'} ended with a resource arriving at hospital. Hours committed run from a resource being assigned to it clearing.`,
            )}
          </Text>
          <DataTable
            columns={[
              { label: 'Call sign', flex: 1.5 },
              { label: 'Type', flex: 1.5 },
              { label: 'Jobs', flex: 0.7, align: 'right' },
              { label: 'Hours committed', flex: 1, align: 'right' },
            ]}
            rows={room.resources.map((r) => [r.callSign, r.type || '-', r.jobs, r.committedHours])}
            emptyText="No resources were booked into the control room"
          />
        </>
      )}

      {part('activity') && (
        <Text style={{ fontSize: 6.5, color: MUTED, marginTop: 6, lineHeight: 1.4 }}>
          {clean(
            `${room.logEntries} control room log entries were made${room.flaggedLogEntries ? `, ${room.flaggedLogEntries} flagged as significant` : ''}, across ${room.open.sessions} session${room.open.sessions === 1 ? '' : 's'} totalling ${room.open.hours} hours.`,
          )}
        </Text>
      )}
    </ReportPage>
  )
}

export const TreatmentLocations = ({ number, data, chrome, charts, include }) => {
  const t = data.treatment
  const part = (key) => include.part('treatment', key)
  return (
    <ReportPage chrome={chrome}>
      <SectionHeading
        number={number}
        title="Treatment locations"
        intro="Patients booked into medical centres, welfare areas and other treatment locations through the control room. Length of stay runs from arrival to discharge or transfer."
      />
      {part('headline') && (
        <StatRow
          items={[
            { label: 'Patients admitted', value: t.overall.patients },
            { label: 'Median stay', value: fmtMinutes(t.overall.lengthOfStay.median), color: TEAL },
            { label: 'Longest stay', value: fmtMinutes(t.overall.lengthOfStay.max) },
            { label: 'Peak at once', value: t.overall.peak.count, sub: t.overall.peak.at ? moment(t.overall.peak.at).format('ddd HH:mm') : null },
            { label: 'Transferred out', value: t.overall.transferred },
          ]}
        />
      )}
      {part('table') && (
        <DataTable
          columns={[
            { label: 'Location', flex: 2 },
            { label: 'Type', flex: 1.3 },
            { label: 'Patients', flex: 0.8, align: 'right' },
            { label: 'Median stay', flex: 1, align: 'right' },
            { label: 'Mean stay', flex: 1, align: 'right' },
            { label: '90th centile', flex: 1, align: 'right' },
            { label: 'Peak at once', flex: 0.9, align: 'right' },
            { label: 'Transfers', flex: 0.9, align: 'right' },
          ]}
          rows={t.locations.map((l) => [
            l.name,
            l.type || '-',
            l.patients,
            fmtMinutes(l.lengthOfStay.median),
            fmtMinutes(l.lengthOfStay.mean),
            fmtMinutes(l.lengthOfStay.p90),
            l.peak.count,
            l.transferred,
          ])}
        />
      )}
      {part('occupancy') &&
        t.locations
          .filter((l) => charts.occupancy?.[l.id])
          .map((l) => (
            <View key={l.id} wrap={false}>
              <SectionTitle>{`${l.name} - patients in the location over time`}</SectionTitle>
              <ChartImage
                image={charts.occupancy[l.id]}
                caption={
                  l.peak.at
                    ? `Peak of ${l.peak.count} at ${fmtDateTime(l.peak.at)}. Sampled every 30 minutes.`
                    : 'Sampled every 30 minutes.'
                }
              />
            </View>
          ))}
    </ReportPage>
  )
}

export const Decisions = ({ number, data, chrome }) => (
  <ReportPage chrome={chrome}>
    <SectionHeading
      number={number}
      title="Significant decisions"
      intro="Decisions recorded in the control room decision log, with the problem they addressed."
    />
    {data.controlRoom.decisions.map((d, i) => (
      <View key={i} style={{ borderBottomWidth: 0.5, borderBottomColor: LINE, paddingVertical: 6 }} wrap={false}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
          <Text style={{ fontSize: 8.5, fontFamily: 'Helvetica-Bold', color: NAVY }}>
            {clean(d.number ? `Decision ${d.number}` : `Decision ${i + 1}`)}
          </Text>
          <Text style={{ fontSize: 6.5, color: MUTED }}>{clean(`${fmtDateTime(d.at)} - ${d.status}`)}</Text>
        </View>
        <Text style={{ fontSize: 7.5, color: INK, marginTop: 3, lineHeight: 1.4 }}>
          <Text style={{ fontFamily: 'Helvetica-Bold' }}>Problem: </Text>
          {clean(d.problem)}
        </Text>
        {d.decision ? (
          <Text style={{ fontSize: 7.5, color: INK, marginTop: 2, lineHeight: 1.4 }}>
            <Text style={{ fontFamily: 'Helvetica-Bold' }}>Decision: </Text>
            {clean(d.decision)}
          </Text>
        ) : null}
      </View>
    ))}
  </ReportPage>
)
